import React, { useState, useEffect, useCallback, useMemo } from 'react';
import {
  Box,
  Checkbox,
  Divider,
  FormControlLabel,
  IconButton,
  Typography,
  Paper,
  ListSubheader,
  Stack,
  Alert,
  TextField,
} from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
 
import { createPermission, updatePermission } from '../../modules/config';
import ActionButton from '../../ui/primitives/ActionButton';
import CustomInputField from '../../ui/primitives/CustomInputField';
import CustomSelect from '../../ui/primitives/CustomSelect';
import Label from '../../ui/primitives/Label';
 
const PermissionRow = React.memo(({ perm, checked, disabled, onChange }) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      px: 2,
      py: 0.5,
      '&:not(:last-child)': {
        borderBottom: '1px solid',
        borderColor: 'action.hover',
      },
    }}>
    {disabled ? (
      <Box
        sx={{
          display: 'flex',
          gap: 1,
          alignItems: 'center',
          py: 0.25,
          width: '100%',
        }}>
        <CheckCircleOutlineIcon sx={{ fontSize: 16 }} color="primary" />
        <Typography
          variant="body2"
          sx={{ fontWeight: 500, color: 'text.primary', fontSize: '13px' }}>
          {perm.formattedName || perm.name}
        </Typography>
      </Box>
    ) : (
      <FormControlLabel
        sx={{ m: 0, width: '100%' }}
        control={
          <Checkbox
            size="small"
            checked={checked}
            onChange={(e) => onChange(perm.id, e.target.checked)}
            sx={{ mr: 0.5, p: 0.5 }}
          />
        }
        label={
          <Typography
            variant="body2"
            sx={{ fontWeight: 500, color: 'text.primary', fontSize: '13px' }}>
            {perm.formattedName || perm.name}
          </Typography>
        }
      />
    )}
  </Box>
));
PermissionRow.displayName = 'PermissionRow';
 
const PermissionList = ({
  permissions,
  checkedIds,
  disabled,
  onChange,
  searchFilter = '',
}) => {
  const filtered = useMemo(() => {
    const term = searchFilter.toLowerCase().trim();
    if (!term) return permissions;
    return permissions.filter(
      (p) =>
        p.formattedName?.toLowerCase().includes(term) ||
        p.name?.toLowerCase().includes(term) ||
        p.feature?.toLowerCase().includes(term),
    );
  }, [permissions, searchFilter]);
 
  const groupedPermissions = useMemo(() => {
    return filtered.reduce((acc, perm) => {
      const group = perm.feature || 'General';
      if (!acc[group]) acc[group] = [];
      acc[group].push(perm);
      return acc;
    }, {});
  }, [filtered]);
 
  return (
    <Paper
  variant="outlined"
  sx={{
    mt: 1,
    maxHeight: 300,
    overflowY: 'auto',
    bgcolor: 'background.default',
    borderRadius: 1,
    '& .MuiListSubheader-root': {
      position: 'sticky',
      top: 0,
      zIndex: 1,
    },
  }}>
      {filtered.length === 0 ? (
        <Typography
          variant="body2"
          sx={{
            color: 'text.secondary',
            p: 2,
            textAlign: 'center',
            fontSize: '13px',
          }}>
          No records found.
        </Typography>
      ) : (
        Object.entries(groupedPermissions).map(([featureName, perms]) => (
          <Box key={featureName}>
            <Box
              sx={{
                px: 2,
                py: 0.75,
                bgcolor: 'action.hover',
                fontWeight: 600,
                fontSize: '11px',
                textTransform: 'uppercase',
                color: 'text.secondary',
                letterSpacing: '0.5px',
              }}>
              {featureName.replaceAll('-', ' ')}
            </Box>
            {perms.map((p) => (
              <PermissionRow
                key={p.id}
                perm={p}
                checked={checkedIds.includes(p.id)}
                disabled={disabled}
                onChange={onChange}
              />
            ))}
          </Box>
        ))
      )}
    </Paper>
  );
};
 
const RoleDrawerPanel = ({
  panel,
  scope,
  permissions,
  onClose,
  onEdit,
  onDelete,
  onSave,
  refetchPermissions,
  isSuperAdmin,
  apiError = '',
  onClearError,
}) => {
  const initialRole = panel?.role;
  const mode = panel?.mode;
 
  const [name, setName] = useState('');
  const [desc, setDesc] = useState('');
  const [perms, setPerms] = useState(new Set());
  const [nameError, setNameError] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [innerSearch, setInnerSearch] = useState('');
 
  const [permName, setPermName] = useState('');
  const [permFeature, setPermFeature] = useState('');
  const [permAction, setPermAction] = useState('');
  const [permScope, setPermScope] = useState('project');
  const [permDesc, setPermDesc] = useState('');
  const [roleScope, setRoleScope] = useState('org');
  const [permApiError, setPermApiError] = useState('');
 
  const actionOptions = [
    { id: 'view', name: 'View' },
    { id: 'manage', name: 'Manage' },
    { id: 'create', name: 'Create' },
    { id: 'delete', name: 'Delete' },
  ];
 
  const scopeOptions = [
    { id: 'project', name: 'Project' },
    { id: 'org', name: 'Organization' },
  ];
 
  useEffect(() => {
    if (initialRole) {
      setName(initialRole.name ?? '');
      setDesc(initialRole.desc ?? '');
      setPerms(new Set(initialRole.perms ?? []));
      setRoleScope(initialRole.scope ?? 'org');
    } else {
      setName('');
      setDesc('');
      setPerms(new Set());
      setRoleScope('org');
    }
    setNameError('');
    setInnerSearch('');
    setSubmitting(false);
    setPermName('');
    setPermFeature('');
    setPermAction('');
    setPermScope('project');
    setPermDesc('');
    setPermApiError('');
 
    if ((mode === 'create-perm' || mode === 'edit-perm') && initialRole) {
      setPermName(initialRole.name ?? '');
      setPermFeature(initialRole.feature ?? '');
      setPermAction(initialRole.action ?? '');
      setPermScope(initialRole.scope ?? 'project');
      setPermDesc(initialRole.description ?? '');
    }
  }, [initialRole, mode]);
 
  const filteredPermissions = useMemo(() => {
    const effectiveScope = isSuperAdmin ? roleScope : scope;
    if (effectiveScope === 'project') return permissions.filter((p) => p.scope === 'project');
    return permissions;
  }, [permissions, roleScope, scope, isSuperAdmin]);

  const togglePerm = useCallback((id, val) => {
    setPerms((prev) => {
      const next = new Set(prev);
      val ? next.add(id) : next.delete(id);
      return next;
    });
  }, []);
 
  const handleSave = async () => {
    if (!name.trim()) {
      setNameError('Role name is required');
      return;
    }
    setSubmitting(true);
    try {
      await onSave({ name: name.trim(), desc: desc.trim(), perms: [...perms], roleScope });
    } catch (err) {
    } finally {
      setSubmitting(false);
    }
  };
 
  const handleCreatePermission = async () => {
    if (!permName.trim() || !permFeature.trim() || !permAction) return;
    setSubmitting(true);
    setPermApiError('');
    try {
      const payload = {
        name: permName.trim(),
        feature: permFeature.trim(),
        action: permAction,
        scope: permScope,
        description: permDesc.trim(),
      };
      if (mode === 'edit-perm' && initialRole?.id) {
        await updatePermission(initialRole.id, payload);
      } else {
        await createPermission(payload);
      }
      if (refetchPermissions) await refetchPermissions();
      onClose();
    } catch (err) {
      setPermApiError(err?.message || 'Something went wrong. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };
 
  if (!panel) return null;
 
  if (mode === 'create-perm' || mode === 'edit-perm') {
    return (
      <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            mb: 1.5,
          }}>
          <Typography variant="h6" sx={{ fontSize: '16px', fontWeight: 600 }}>
            {mode === 'edit-perm' ? 'Edit Permission' : 'Create New Permission'}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseOutlinedIcon fontSize="small" />
          </IconButton>
        </Box>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, mt: 2 }}>
          <CustomInputField
            id="permName"
            label="Permission Name"
            placeholder="e.g., View Test Suites"
            value={permName}
            onChange={(_, val) => setPermName(val)}
            required
            maxLength={100}
          />
 
          <CustomInputField
            id="permFeature"
            label="Feature Name"
            placeholder="e.g., test-suites"
            value={permFeature}
            onChange={(_, val) => setPermFeature(val)}
            required
            maxLength={100}
          />
 
          <CustomSelect
            id="permAction"
            label="Action"
            placeholder="Select action type"
            value={permAction}
            options={actionOptions}
            onChange={(val) => setPermAction(val)}
            required
          />
 
          <CustomSelect
            id="permScope"
            label="Permission Scope"
            value={permScope}
            options={scopeOptions}
            onChange={(val) => setPermScope(val)}
            required
          />
 
          <CustomInputField
            id="permDesc"
            label="Description"
            placeholder="e.g., View project test suites"
            value={permDesc}
            onChange={(_, val) => setPermDesc(val)}
            maxLength={200}
            multiline
            rows={2}
          />
        </Box>
        <Divider sx={{ my: 2 }} />
        {permApiError && (
          <Alert severity="error" onClose={() => setPermApiError('')} sx={{ mb: 2, py: 0.5, fontSize: '13px' }}>
            {permApiError}
          </Alert>
        )}
        <Stack
          direction="row"
          sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
          <ActionButton
            variant="outlined"
            size="medium"
            onClick={onClose}
            label="Cancel"
            tooltip="Cancel"
          />
          <ActionButton
            variant="contained"
            size="medium"
            onClick={handleCreatePermission}
            label={submitting ? 'Saving…' : mode === 'edit-perm' ? 'Save Permission' : 'Create Permission'}
            disabled={
              submitting ||
              !permName.trim() ||
              !permFeature.trim() ||
              !permAction
            }
            tooltip="Save Permission"
          />
        </Stack>
      </Box>
    );
  }
 
  const isView = mode === 'view-sys' || mode === 'view-custom';
 
  return (
    <Box sx={{ p: 2, height: '100%', overflowY: 'auto' }}>
      {isView && initialRole ? (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{ fontSize: '16px', fontWeight: 600 }}>
                {initialRole.name}
              </Typography>
              <Label name={initialRole.isSystemRole ? 'System' : scope} />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
 
          {initialRole.isSystemRole && !isSuperAdmin && (
            <Alert
              icon={<LockOutlinedIcon fontSize="small" />}
              severity="info"
              sx={{ mb: 1.5, py: 0, px: 1.5, fontSize: '12px' }}>
              System roles are managed by the platform and cannot be directly
              modified or deleted.
            </Alert>
          )}
 
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontSize: '11px', fontWeight: 600 }}>
            Permissions
          </Typography>
 
          <TextField
            fullWidth
            size="small"
            placeholder="Search permissions..."
            value={innerSearch}
            onChange={(e) => setInnerSearch(e.target.value)}
            sx={{
              mb: 1,
              mt: 0.5,
              '& .MuiInputBase-input': { fontSize: '13px' },
            }}
          />
 
          <PermissionList
            permissions={permissions.filter((p) => initialRole.perms?.includes(p.id))}
            checkedIds={initialRole.perms}
            disabled={true}
            onChange={togglePerm}
            searchFilter={innerSearch}
          />
 
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', gap: 1.5, alignItems: 'center' }}>
            {!initialRole.isSystemRole && (
              <IconButton color="error" onClick={onDelete} size="small">
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            )}
            <Box sx={{ ml: 'auto', display: 'flex', gap: 1.5 }}>
              {(!initialRole.isSystemRole || isSuperAdmin) && (
                <ActionButton
                  variant="outlined"
                  size="medium"
                  onClick={onEdit}
                  label="Edit Role"
                  tooltip="Edit Role"
                />
              )}
              <ActionButton
                variant="contained"
                size="medium"
                onClick={onClose}
                label="Close"
                tooltip="Close"
              />
            </Box>
          </Box>
        </>
      ) : (
        <>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5,
            }}>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
              <Typography
                variant="h6"
                sx={{ fontSize: '16px', fontWeight: 600 }}>
                {mode === 'edit'
                  ? 'Edit Custom Role'
                  : `New ${isSuperAdmin ? 'System' : 'Custom'} Role`}
              </Typography>
              <Label name={isSuperAdmin ? 'System' : scope} />
            </Box>
            <IconButton onClick={onClose} size="small">
              <CloseOutlinedIcon fontSize="small" />
            </IconButton>
          </Box>
 
          <Box sx={{ display: 'flex', flexDirection: 'column' }}>
           <CustomInputField
              id="name"
              label="Role Name"
              value={name}
              onChange={(_, value) => {
                setName(value);
                if (nameError) setNameError('');
              }}
              required
              maxLength={100}
              error={nameError}
              disabled={isSuperAdmin && mode === 'edit'}
            />
            <CustomInputField
              id="description"
              label="Description"
              value={desc}
              onChange={(_, value) => setDesc(value)}
              maxLength={200}
            />
            {isSuperAdmin && (
              <CustomSelect
                id="roleScope"
                label="Role Scope"
                value={roleScope}
                options={[{ id: 'org', name: 'Organization' }, { id: 'project', name: 'Project' }]}
                onChange={(val) => setRoleScope(val)}
                required
              />
            )}
            <Alert severity="info" sx={{ mt: 1, py: 0.5, fontSize: '13px' }}>
              {(roleScope === 'org') ? (
                <>Users with this role will get access to all projects automatically.</>
              ) : (
                 <>Users must be assigned to each project individually.</>
              )}
            </Alert>
          </Box>
 
          <Divider sx={{ mb: 1 }} />
          <Typography
            variant="overline"
            sx={{ color: 'text.secondary', fontSize: '11px', fontWeight: 600 }}>
            Assign permissions
          </Typography>
 
          <TextField
            fullWidth
            size="small"
            placeholder="Search permissions..."
            value={innerSearch}
            onChange={(e) => setInnerSearch(e.target.value)}
            sx={{
              mb: 1,
              mt: 0.5,
              '& .MuiInputBase-input': { fontSize: '13px' },
            }}
          />
 
          <PermissionList
            permissions={filteredPermissions}
            checkedIds={[...perms]}
            disabled={false}
            onChange={togglePerm}
            searchFilter={innerSearch}
          />
          <Divider sx={{ my: 2 }} />
          {apiError && (
            <Alert severity="error" onClose={onClearError} sx={{ mb: 2, py: 0.5, fontSize: '13px' }}>
              {apiError}
            </Alert>
          )}
          <Stack
            direction="row"
            sx={{ display: 'flex', justifyContent: 'center', gap: 1 }}>
            <ActionButton
              variant="outlined"
              size="medium"
              onClick={onClose}
              label="Cancel"
              tooltip="Cancel"
            />
            <ActionButton
              variant="contained"
              size="medium"
              onClick={handleSave}
              label={
                submitting
                  ? 'Saving…'
                  : mode === 'edit'
                    ? 'Save changes'
                    : 'Create role'
              }
              disabled={submitting}
              tooltip="Save"
            />
          </Stack>
        </>
      )}
    </Box>
  );
};
 
export default RoleDrawerPanel;
